import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Záchytná síť pro chyby při vykreslování. Bez ní React odmountuje celý strom
 * a zůstane bílé okno bez jediné informace — uživatel neví, jestli spadla appka,
 * síť, nebo prohlížeč.
 *
 * Nejčastější příčina v provozu: otevřená záložka s aplikací, která běží ještě ve
 * staré verzi, zatímco backend už odpovídá novým tvarem dat. Proto je hlavní
 * nabízená akce obnovení stránky.
 */
interface Props {
  children: ReactNode;
  // Kde se to stalo — pomáhá rozlišit pád stránky od pádu celé aplikace.
  scope?: string;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Do konzole vypíšeme i komponentový stack — z minifikovaného hlášení
    // samotného se v produkci nepozná, která stránka spadla.
    console.error(`[chyba vykreslení${this.props.scope ? ` — ${this.props.scope}` : ''}]`, error, info.componentStack);
    // Správce se to má dozvědět, i když to člověk nenahlásí. Nepodaří-li se to, nic se neděje.
    void fetch('/api/notifications/client-error', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ message: (error.message || String(error)).slice(0, 300), scope: this.props.scope?.slice(0, 120) }),
    }).catch(() => undefined);
  }

  private reload = () => {
    // Tvrdé obnovení: zahodí i rozpracovaný stav, o to tady jde.
    window.location.reload();
  };

  private back = () => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto mt-10 max-w-lg rounded-xl border border-red-200 dark:border-red-900/50 bg-white dark:bg-zinc-900 p-6">
        <h1 className="text-base font-semibold">Tuhle část se nepodařilo zobrazit</h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
          Nejčastěji za to může okno otevřené ještě ve starší verzi aplikace. Obnovení stránky
          to skoro vždycky spraví.
        </p>

        <pre className="mt-3 overflow-x-auto rounded-lg bg-zinc-100 dark:bg-zinc-800 p-3 text-xs text-zinc-700 dark:text-zinc-300">
          {error.message || String(error)}
        </pre>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={this.reload}
            className="inline-flex h-9 items-center rounded-lg bg-zinc-900 dark:bg-zinc-100 px-4 text-sm font-medium text-white dark:text-zinc-900"
          >
            Načíst znovu
          </button>
          <button
            type="button"
            onClick={this.back}
            className="inline-flex h-9 items-center rounded-lg border border-zinc-300 dark:border-zinc-700 px-4 text-sm font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Zkusit znovu bez obnovení
          </button>
        </div>

        <p className="mt-3 text-xs text-zinc-400">
          Podrobnosti včetně toho, která komponenta spadla, jsou v konzoli prohlížeče.
        </p>
      </div>
    );
  }
}
